/**
 * Group API Service
 *
 * API methods for group management and member operations.
 * Updated to use standardized API response format.
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from '../types/api.types';
import type {
  GroupSanitized,
  GroupCreateDTO,
  GroupUpdateDTO,
  GroupAddUserDTO,
  GroupListQuery,
} from '../types/group.types';

// ============================================================================
// GROUP SERVICE
// ============================================================================

export const groupService = {
  /**
   * Create a new group within tenant
   */
  createGroup: async (tenantId: string, data: GroupCreateDTO): Promise<GroupSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<GroupSanitized>>(
      `/tenants/${tenantId}/groups`,
      data
    );
    return response.data.data;
  },

  /**
   * List all groups in tenant
   */
  listGroups: async (tenantId: string, query?: GroupListQuery): Promise<GroupSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<GroupSanitized>>(
      `/tenants/${tenantId}/groups`,
      {
        params: query,
      }
    );
    return response.data.data;
  },

  /**
   * Get specific group details
   */
  getGroup: async (tenantId: string, groupId: string): Promise<GroupSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<GroupSanitized>>(
      `/tenants/${tenantId}/groups/${groupId}`
    );
    return response.data.data;
  },

  /**
   * Update group information (name, description)
   */
  updateGroup: async (
    tenantId: string,
    groupId: string,
    data: GroupUpdateDTO
  ): Promise<GroupSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<GroupSanitized>>(
      `/tenants/${tenantId}/groups/${groupId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete group and remove from all members
   */
  deleteGroup: async (tenantId: string, groupId: string): Promise<void> => {
    await apiClient.delete(`/tenants/${tenantId}/groups/${groupId}`);
  },

  /**
   * Add a user to a group (bidirectional update)
   */
  addUser: async (
    tenantId: string,
    groupId: string,
    data: GroupAddUserDTO
  ): Promise<{ message: string; groupId: string; userId: string; updatedAt: string }> => {
    const response = await apiClient.post<ApiActionResponse>(
      `/tenants/${tenantId}/groups/${groupId}/members`,
      data
    );
    return {
      message: response.data.message,
      groupId: (response.data.data?.groupId as string) || groupId,
      userId: (response.data.data?.userId as string) || data.userId,
      updatedAt: (response.data.data?.updatedAt as string) || new Date().toISOString(),
    };
  },

  /**
   * Remove a user from a group (bidirectional update)
   */
  removeUser: async (
    tenantId: string,
    groupId: string,
    userId: string
  ): Promise<{ message: string; groupId: string; userId: string; updatedAt: string }> => {
    const response = await apiClient.delete<ApiActionResponse>(
      `/tenants/${tenantId}/groups/${groupId}/members/${userId}`
    );
    return {
      message: response.data.message,
      groupId: (response.data.data?.groupId as string) || groupId,
      userId: (response.data.data?.userId as string) || userId,
      updatedAt: (response.data.data?.updatedAt as string) || new Date().toISOString(),
    };
  },
};
